import React, { Fragment, useState, useMemo } from 'react'
import { Box, FormHelperText, TextareaAutosize, TextField, Tooltip } from '@vapor/material'
import { Stack, SxProps, Typography } from '@mui/material'
import { Language, LocalizedString, MultiLanguagesInputProps } from './multi-languages-types'
import MultiLanguagesDialog from './multi-languages-dialog'
import { MultiLanguagesButton } from './multi-languages-commons'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const MultiLanguagesInput = (props: MultiLanguagesInputProps) => {
  const {
    label,
    labelId,
    languages,
    mainLanguage,
    defaultValue,
    value: valueFromProps,
    onChange: onChangeFromProps,
    pendingForTranslation: pendingForTranslation,
    languagesHidden,
    multiline,
    minRows,
    error,
    borderError,
    placeholder,
    tooltipLabel,
    buttonTooltipLabel,
    disabled,
    size,
  } = props

  const isControlled = typeof valueFromProps != 'undefined'
  const hasDefaultValue = typeof defaultValue != 'undefined'

  if (isControlled && hasDefaultValue)
    console.error(
      "'MultiLanguagesInput' contains an input with both value and defaultValue props. Input elements must be either controlled or uncontrolled (specify either the value prop, or the defaultValue prop, but not both). Decide between using a controlled or uncontrolled input element and remove one of these props. More info: https://reactjs.org/link/controlled-components",
    )

  const [internalValue, setInternalValue] = useState<LocalizedString | undefined | null>(hasDefaultValue ? defaultValue : [])

  const value = isControlled ? valueFromProps : internalValue

  const ellipsis: SxProps = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let changedValue: LocalizedString | null | undefined = null
    if (!isControlled) {
      if (internalValue) {
        changedValue = [...internalValue?.filter((x) => x.language !== mainLanguage), { language: mainLanguage, value: e.target.value }].filter((i) => i.value !== '')
      } else if (e.target.value) {
        changedValue = [{ language: mainLanguage, value: e.target.value }]
      }
      setInternalValue(changedValue?.length ? changedValue : null)
    } else {
      if (value) {
        changedValue = [...value?.filter((x) => x.language !== mainLanguage), { language: mainLanguage, value: e.target.value }].filter((i) => i.value)
      } else if (e.target.value) {
        changedValue = [{ language: mainLanguage, value: e.target.value }]
      }
    }
    changedValue?.sort((a) => (a.language === mainLanguage ? -1 : 0))
    onChangeFromProps(changedValue?.length ? changedValue : null)
  }

  const [openDialog, setOpenDialog] = useState<boolean>(false)

  const languagesComponent = useMemo(() => {
    return (
      <Box sx={{ flex: '0 1 auto', ...ellipsis, pr: 1 }}>{!languagesHidden && <ListLanguages languages={languages} translated={value?.filter((v) => v.value).map((l) => l.language) ?? []} />}</Box>
    )
  }, [languagesHidden, languages, value])

  return (
    <Box component="span" display="flex" flexDirection="column" width="100% !important">
      <Box component="div" display="flex" justifyContent="space-between" sx={{ alignItems: 'center' }}>
        <Box component="span" sx={{ flex: '1 1 auto', pr: 1, ...ellipsis }}>
          <Tooltip title={tooltipLabel ?? label} placement="top-start">
            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle2" component="label" id={labelId}>
                {label}
              </Typography>
              {tooltipLabel && (
                <Box>
                  <FontAwesomeIcon icon={faCircleInfo} size="1x" height={'20px'} color="#5A6872" />
                </Box>
              )}
            </Stack>
          </Tooltip>
        </Box>
        <Box
          display="flex"
          justifyContent="flex-end"
          flex="1 1 auto"
          minWidth={`calc(${languagesHidden ? '0px' : '40px'} + 30px)`}
          sx={{ cursor: 'pointer', height: '22px' /* same as the label for FormItem */ }}
          alignItems="center"
          onClick={() => setOpenDialog(true)}
        >
          {buttonTooltipLabel ? (
            <Tooltip title={buttonTooltipLabel}>
              <div>{languagesComponent}</div>
            </Tooltip>
          ) : (
            languagesComponent
          )}
          <MultiLanguagesButton loading={pendingForTranslation ?? false} tooltip={buttonTooltipLabel} />
        </Box>
      </Box>
      <Box display="flex">
        {!props.asTextArea && (
          <TextField
            id="outlined-basic"
            variant="outlined"
            fullWidth
            value={value && value.length ? value?.find((l) => l.language === mainLanguage)?.value ?? '' : ''}
            onChange={handleOnChange}
            disabled={pendingForTranslation || disabled}
            multiline={multiline}
            minRows={minRows}
            error={error !== undefined || borderError}
            placeholder={placeholder}
            size={size}
          />
        )}
        {props.asTextArea && (
          <TextareaAutosize
            id="outlined-basic-area"
            style={{ resize: props.asTextArea.disableResize ? 'none' : undefined, height: props.asTextArea.disableResize ? '72px' : undefined, overflow: 'visible', width: '100%' }}
            value={value && value.length ? value?.find((l) => l.language === mainLanguage)?.value ?? '' : ''}
            onChange={handleOnChange}
            disabled={pendingForTranslation || disabled}
            placeholder={placeholder}
          />
        )}
      </Box>
      {error && (
        <FormHelperText id="multilanguage-input-error" error>
          {error}
        </FormHelperText>
      )}

      <MultiLanguagesDialog
        {...props}
        languages={languages}
        value={value ?? []}
        open={openDialog}
        handleClose={() => {
          setOpenDialog(false)
        }}
        handleConfirm={(v) => {
          if (onChangeFromProps) {
            onChangeFromProps(v)
          }

          if (!isControlled) {
            setInternalValue(v)
          }
        }}
      />
    </Box>
  )
}

const ListLanguages = ({ languages, translated }: { languages: Language[]; translated: Language[] }) => {
  return (
    <>
      {languages.map((l, key, list) => {
        const isTranslated = translated.findIndex((x) => x === l) !== -1
        return (
          <Fragment key={key}>
            <Typography component="span" variant="overline" sx={{ color: isTranslated ? 'primary' : '#ccc' }}>
              {` ${l.toUpperCase()} `}
            </Typography>
            {list.length - 1 !== key ? '|' : ''}
          </Fragment>
        )
      })}
    </>
  )
}

export default MultiLanguagesInput
